import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import Dialog from "@mui/material/Dialog"
import DialogTitle from "@mui/material/DialogTitle"
import DialogContent from "@mui/material/DialogContent"
import DialogActions from "@mui/material/DialogActions"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { Plus, Receipt, Clock, PackageSearch, Download } from "lucide-react"
import { Page, Button } from "./ui"
import {
  FilterBar,
  DataTable,
  StatusPill,
  MoneyText,
  DateText,
  KpiTile,
  KpiStrip,
  EmptyState,
  en,
} from "procurement/design"
import useProcurementFilters from "./hooks/useProcurementFilters"
import useProcurementListFilters from "./hooks/useProcurementListFilters"
import useIsDepartmentManager from "shared/procurement/useIsDepartmentManager"
import { useGetPaymentRequestsQuery, useDeletePaymentRequestMutation } from "shared/redux/endpoints/procurementPayments"
import { useGetPurchaseSuppliersQuery } from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { PAYMENT_REQUEST_STATUS, PAYMENT_REQUEST_STATUS_LABEL } from "./status"
import ClampedText from "./components/ClampedText"
import { analysisLabel } from "./components/payment/budgetAllocation"
import { useDownloadPaymentRequestAllocation } from "./components/payment/PaymentBudgetAllocation"

const STATUS_OPTIONS = Object.entries(PAYMENT_REQUEST_STATUS_LABEL).map(([value, label]) => ({ value, label }))

// `fromDate`/`toDate` filter by created_at (the shared OR/PO/PR filter set,
// via buildProcurementListFilters) — not invoice_date. The API also supports
// periodFrom/periodTo (invoice_date) but that isn't a required field on a PR
// so this list doesn't filter by it; revisit if that turns out to be needed.
const FILTER_DEFAULTS = {
  search: "",
  status: "",
  supplier: "",
  category: "",
  region: "",
  center: "",
  department: "",
  item: "",
  fromDate: "",
  toDate: "",
  awaiting: "",
}

// F4 — Payment Request List. Every payment request (batch) across the org,
// filterable by status/supplier/PO code/date range (PO code is matched
// through the free-text search box alongside PR code and supplier). Built on
// the same procurement/design primitives as Orders (F3) so the two
// workspaces read as one product.
//
// `standalone`: renders under the requester-side Procurement tab with a
// plain breadcrumb, same as the manager workspace but without the
// awaiting/create actions being manager-only.
//
// `approvalOnly`: renders the same list forced to `awaiting_me=true` with no
// toggle (create button stays, Finance creates from here) — the dedicated "Approve Payment Request" screen,
// backed by the real GET /payment-requests?awaiting_me=true (current-step
// approver only, escalation/delegation-aware — not just ?status=pending).
//
// `centerScoped` (LXO): the center filter lists only the viewer's linked
// centers and can't be cleared — there's no "All", and reset falls back to the
// first linked center. Department managers skip it: BE already narrows their
// list to their departments, which can span centers.
const PaymentRequestsContainer = ({ standalone = false, approvalOnly = false, centerScoped: centerScopedProp = false, title, breadcrumb }) => {
  const navigate = useNavigate()
  const isDepartmentManager = useIsDepartmentManager()
  const centerScoped = centerScopedProp && !isDepartmentManager
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const awaiting = approvalOnly || filters.awaiting === "1"

  const { categoryOptions, regionOptions, centerOptions, centersLoading, departmentOptions, itemOptions, itemsLoading, queryFilters } =
    useProcurementListFilters(filters, { userCenters: centerScoped })
  // The sidebar center switch drives this page: it is the default, and
  // switching it overrides a center picked here (or carried in the URL).
  const switchedCenter = String(useSelector((state) => state.auth.centerId) ?? "")
  const findCenter = (v) => centerOptions.find((o) => o.value === v)
  const scopedCenter = centerScoped
    ? (findCenter(filters.center) || findCenter(switchedCenter) || centerOptions[0])?.value || ""
    : null
  const lastSwitched = useRef(switchedCenter)
  useEffect(() => {
    if (!centerScoped || lastSwitched.current === switchedCenter) return
    lastSwitched.current = switchedCenter
    setFilters({ center: switchedCenter })
  }, [centerScoped, switchedCenter, setFilters])
  const noLinkedCenter = centerScoped && !centersLoading && !scopedCenter
  const newPaymentPath = `/procurement/payment-requests/new${scopedCenter ? `?center=${scopedCenter}` : ""}`

  const { data, isFetching, isError, refetch } = useGetPaymentRequestsQuery(
    {
      ...queryFilters,
      ...(centerScoped && { center: scopedCenter }),
      status: filters.status || undefined,
      supplier: filters.supplier || undefined,
      awaitingMe: awaiting || undefined,
    },
    { skip: centerScoped && !scopedCenter }
  )
  const { data: suppliers } = useGetPurchaseSuppliersQuery()
  const supplierOptions = useMemo(
    () => (suppliers || []).map((s) => ({ value: String(s.id), label: s.name })),
    [suppliers]
  )

  const [deletePr, { isLoading: deleting }] = useDeletePaymentRequestMutation()
  const [deleteTarget, setDeleteTarget] = useState(null)

  const handleDelete = async () => {
    try {
      await deletePr(deleteTarget.id).unwrap()
      showToastSuccess("Draft deleted.")
    } catch (e) {
      showToastError(e?.data?.data?.message || e?.data?.message || "Couldn't delete this draft.")
    } finally {
      setDeleteTarget(null)
    }
  }

  const downloadAllocation = useDownloadPaymentRequestAllocation()
  const [downloadingId, setDownloadingId] = useState(null)

  const handleDownload = useCallback(
    async (id, paymentCode) => {
      setDownloadingId(id)
      try {
        const count = await downloadAllocation(id, paymentCode)
        if (!count) showToastError("This payment request has no budget allocation yet.")
      } catch {
        showToastError("Couldn't generate the Excel file. Please try again.")
      } finally {
        setDownloadingId(null)
      }
    },
    [downloadAllocation]
  )

  const rows = useMemo(() => {
    const items = (data?.items || []).map((r) => ({
      ...r,
      key: r.id,
      purchaseCode: (r.poCodes || []).join(", "),
      supplierName: (r.supplierNames || []).join(", "),
    }))
    const q = filters.search.trim().toLowerCase()
    return q
      ? items.filter((r) => `${r.paymentCode} ${r.purchaseCode} ${r.supplierName}`.toLowerCase().includes(q))
      : items
  }, [data, filters.search])

  const pendingCount = rows.filter((r) => r.status === PAYMENT_REQUEST_STATUS.PENDING).length
  const hasActiveFilter = !!(
    filters.search ||
    filters.status ||
    filters.supplier ||
    filters.category ||
    filters.region ||
    (!centerScoped && filters.center) ||
    filters.item ||
    filters.fromDate ||
    filters.toDate
  )

  const columns = useMemo(
    () => [
      { key: "paymentCode", header: en.paymentRequests.prCode, strong: true, sortable: true },
      { key: "purchaseCode", header: en.paymentRequests.poCode, render: (r) => r.purchaseCode || "—" },
      { key: "batchLabel", header: en.paymentRequests.batch, render: (r) => r.batchLabel || "—" },
      { key: "supplierName", header: en.paymentRequests.supplier, render: (r) => <ClampedText lines={r.supplierNames || []} /> },
      {
        key: "analysisCodes",
        header: en.paymentRequests.analysisCode,
        render: (r) => (
          <ClampedText
            lines={(r.analysisCodes || []).map((a) => [a.sunCenterCode, a.accountCode, analysisLabel(a)].filter(Boolean).join(" · "))}
          />
        ),
      },
      {
        key: "centers",
        header: en.paymentRequests.center,
        render: (r) => <ClampedText lines={(r.centers || []).map((c) => c.centerName)} />,
      },
      {
        key: "invoiceDate",
        header: en.paymentRequests.invoiceDate,
        render: (r) => (r.invoiceDate ? <DateText value={r.invoiceDate} /> : "—"),
      },
      { key: "invoiceSerial", header: en.paymentRequests.invoiceSerial, render: (r) => r.invoiceSerial || "—" },
      {
        key: "amount",
        header: en.paymentRequests.amount,
        align: "right",
        sortable: true,
        render: (r) => <MoneyText value={r.amount} />,
      },
      {
        key: "createdAt",
        header: en.paymentRequests.created,
        sortable: true,
        render: (r) => <DateText value={r.createdAt} />,
      },
      {
        key: "status",
        header: en.paymentRequests.status,
        sortable: true,
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: "actions",
        header: "",
        align: "right",
        render: (r) => (
          <>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download aria-hidden />}
              disabled={downloadingId === r.id}
              onClick={(e) => {
                e.stopPropagation()
                handleDownload(r.id, r.paymentCode)
              }}
            >
              {downloadingId === r.id ? "Preparing…" : "Excel"}
            </Button>
            {r.status === PAYMENT_REQUEST_STATUS.DRAFT && (
              <Button
                variant="danger-outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleteTarget(r)
                }}
              >
                {en.paymentRequests.delete}
              </Button>
            )}
          </>
        ),
      },
    ],
    [downloadingId, handleDownload]
  )

  const tabOptions = [
    { value: "", label: en.paymentRequests.all },
    { value: "1", label: en.paymentRequests.awaitingMe },
  ]

  return (
    <Page
      breadcrumb={
        approvalOnly
          ? ["Procurement", "Payment requests", en.paymentRequests.approveTitle]
          : ["Procurement", en.paymentRequests.title]
      }
      title={approvalOnly ? en.paymentRequests.approveTitle : title || en.paymentRequests.title}
      actions={
        <Button
          variant="primary"
          leftIcon={<Plus aria-hidden />}
          disabled={centerScoped && !scopedCenter}
          onClick={() => navigate(newPaymentPath)}
        >
          {en.paymentRequests.newPayment}
        </Button>
      }
    >
      <KpiStrip>
        <KpiTile
          icon={<Receipt aria-hidden />}
          label={approvalOnly ? en.paymentRequests.awaitingMe : en.paymentRequests.visiblePrs}
          value={isFetching ? "—" : rows.length}
        />
        {!approvalOnly && (
          <KpiTile
            icon={<Clock aria-hidden />}
            label={en.paymentRequests.pendingApproval}
            value={isFetching ? "—" : pendingCount}
            onClick={() => setFilters({ status: PAYMENT_REQUEST_STATUS.PENDING })}
          />
        )}
      </KpiStrip>

      <FilterBar
        title={en.filter.titlePayments}
        values={centerScoped ? { ...filters, center: scopedCenter } : filters}
        onChange={setFilters}
        onReset={resetFilters}
        fields={[
          { key: "search", label: en.filter.search, placeholder: en.filter.searchPaymentRequests },
          ...(approvalOnly
            ? []
            : [
                {
                  key: "awaiting",
                  label: en.filter.view,
                  type: "select",
                  allowEmpty: false,
                  options: tabOptions,
                },
              ]),
          { key: "status", label: en.filter.status, type: "select", options: STATUS_OPTIONS, placeholder: en.filter.allStatuses },
          { key: "supplier", label: en.filter.supplier, type: "select", options: supplierOptions, placeholder: en.filter.allSuppliers },
          { key: "category", label: en.filter.category, type: "select", options: categoryOptions, placeholder: en.filter.allCategories },
          ...(centerScoped
            ? [
                {
                  key: "center",
                  label: en.filter.center,
                  type: "select",
                  options: centerOptions,
                  allowEmpty: false,
                  defaultValue: scopedCenter,
                },
              ]
            : [
                { key: "region", label: en.filter.region, type: "select", options: regionOptions },
                { key: "center", label: en.filter.center, type: "select", options: centerOptions, placeholder: en.filter.allCenters },
              ]),
          { key: "department", label: en.filter.department, type: "select", options: departmentOptions, placeholder: en.filter.allDepartments },
          { key: "item", label: en.filter.item, type: "multiselect", options: itemOptions, loading: itemsLoading, placeholder: en.filter.allItems, searchPlaceholder: en.filter.searchItem },
          { key: "fromDate", label: en.filter.createdFrom, type: "date" },
          { key: "toDate", label: en.filter.createdTo, type: "date" },
        ]}
      />
      {filters.fromDate && filters.toDate && filters.toDate < filters.fromDate && (
        <p className="pc-hint" style={{ margin: "-8px 0 12px", color: "var(--pc-danger)" }}>
          {en.filter.invalidDateRange}
        </p>
      )}

      <div className="ds-table-toolbar">
        <span>{en.common.results(rows.length)}</span>
      </div>

      {noLinkedCenter ? (
        <EmptyState
          icon={<PackageSearch aria-hidden />}
          title="No linked center"
          text="Your account isn't linked to any center, so there are no payment requests to show."
        />
      ) : isError ? (
        <EmptyState
          icon={<PackageSearch aria-hidden />}
          title="Couldn't load payment requests"
          text="The service didn't respond. Try again in a moment."
          action={
            <Button variant="secondary" size="sm" onClick={refetch}>
              {en.common.retry}
            </Button>
          }
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          isLoading={isFetching}
          onRowClick={(r) => navigate(`/procurement/payment-requests/${r.id}`)}
          empty={
            <EmptyState
              icon={<PackageSearch aria-hidden />}
              title={
                awaiting
                  ? en.paymentRequests.emptyAwaitingTitle
                  : hasActiveFilter
                  ? en.paymentRequests.emptyFilteredTitle
                  : en.paymentRequests.emptyTitle
              }
              text={
                awaiting
                  ? en.paymentRequests.emptyAwaitingText
                  : hasActiveFilter
                  ? en.empty.noResultsText
                  : en.paymentRequests.emptyText
              }
              action={
                hasActiveFilter ? (
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    {en.common.clearFilters}
                  </Button>
                ) : !approvalOnly ? (
                  <Button
                    variant="primary"
                    leftIcon={<Plus aria-hidden />}
                    disabled={centerScoped && !scopedCenter}
                    onClick={() => navigate(newPaymentPath)}
                  >
                    {en.paymentRequests.newPayment}
                  </Button>
                ) : null
              }
            />
          }
        />
      )}

      {deleteTarget && (
        <Dialog open onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
          <DialogTitle>{en.paymentRequests.deleteConfirmTitle(deleteTarget.paymentCode)}</DialogTitle>
          <DialogContent>
            <MDTypography variant="body2" color="text">
              {en.paymentRequests.deleteConfirmText}
            </MDTypography>
          </DialogContent>
          <DialogActions>
            <MDButton variant="text" color="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </MDButton>
            <MDButton variant="gradient" color="error" onClick={handleDelete} disabled={deleting}>
              {deleting ? "Deleting…" : "Delete"}
            </MDButton>
          </DialogActions>
        </Dialog>
      )}
    </Page>
  )
}

export default PaymentRequestsContainer
